import React from "react";
import { CheckCircle, CloudUpload, Loader2, Triangle } from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const statusStyles = {
  saving: {
    icon: <Loader2 className="w-5 h-5 animate-spin text-white" />,
    title: "Saving to cloud…",
    tone: "bg-rose-600",
    text: "Please keep this tab open until we finish uploading your invoice.",
  },
  success: {
    icon: <CheckCircle className="w-5 h-5 text-emerald-800" />,
    title: "Saved to cloud",
    tone: "bg-emerald-100",
    textColor: "text-emerald-950",
    descColor: "text-emerald-900",
    borderClass: "border border-emerald-200",
    closeColor: "text-emerald-800",
    closeHover: "hover:text-emerald-950",
    text: "Your invoice was uploaded successfully.",
  },
  error: {
    icon: <Triangle className="w-5 h-5 text-rose-100" />,
    title: "Upload failed",
    tone: "bg-rose-700",
    text: "We couldn’t save the invoice. Please retry.",
  },
};

const ToastBody = ({ status, errorMessage, pendingCount, onClose }) => {
  if (!statusStyles[status]) return null;
  const style = statusStyles[status];
  const canDismiss = status !== "saving";
  const textColor = style.textColor || "text-white";
  const descColor = style.descColor || "text-white/90";
  const borderClass = style.borderClass || "border border-white/10";
  const closeColor = style.closeColor || "text-white/80";
  const closeHover = style.closeHover || "hover:text-white";

  return (
    <div
      className={`flex items-start gap-3 rounded-2xl px-4 py-3 ${style.tone} ${textColor} ${borderClass} shadow-lg backdrop-blur-sm min-w-[320px]`}
    >
      <div className="mt-0.5 flex-shrink-0">{style.icon}</div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold tracking-tight">
            {style.title}
          </span>
          {pendingCount > 1 && status === "saving" && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-white/15">
              {pendingCount} in queue
            </span>
            )}
          </div>
          <p className={`text-xs ${descColor} leading-snug mt-0.5`}>
            {status === "error" && errorMessage ? errorMessage : style.text}
          </p>
      </div>
      {canDismiss && (
        <button
          onClick={onClose}
          className={`${closeColor} ${closeHover} transition-colors flex-shrink-0 mt-0.5`}
          aria-label="Dismiss notification"
        >
          <CloudUpload className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export const showCloudSaveToast = ({
  status,
  errorMessage = "",
  pendingCount = 0,
  toastId = "invoice-upload",
}) => {
  const dismiss = () => toast.dismiss(toastId);
  toast.custom(
    (t) => (
      <div
        className={`${
          t.visible ? "animate-enter" : "animate-leave"
        } pointer-events-auto`}
      >
        <ToastBody
          status={status}
          errorMessage={errorMessage}
          pendingCount={pendingCount}
          onClose={dismiss}
        />
      </div>
    ),
    {
      id: toastId,
      duration: status === "saving" ? Infinity : 4000,
      position: "top-center",
    }
  );
};

export const CloudSaveToaster = () => (
  <Toaster
    position="top-center"
    toastOptions={{
      style: { background: "transparent", boxShadow: "none", padding: 0 },
      duration: 4000,
    }}
  />
);

export default ToastBody;
